import React, { useContext } from 'react';
import styled from 'styled-components';
import { Summary as OrderSummary, StepName } from '|/app/services';
import useNavigateToStep from '|/app/steps/use-navigate-to-step';
import SummaryContext from '|/app/summary-context';
import { percent } from '|/helpers';
import currency from '|/helpers/currency';
import useOrderSummaryVm from '|/helpers/use-order-summary';
import * as Summary from '../../order-summary-components.styles';

const PaymentDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const defaultProps = {
  summary: {} as OrderSummary
};

const PaymentSection = ({ summary } = defaultProps) => {
  const navigateToStep = useNavigateToStep();
  const { setShowSummary } = useContext(SummaryContext);
  const {
    isCarvanaFinancing,
    isOutsideFinance,
    isCash,
    isThirdParty,
    hasPaymentMethodBeenSelected,
    hasTerms,
    fundingInstitution: institution,
    lenderName,
    amountFinanced,
    cashDown,
    loanApr: apr,
    loanTerm: term,
    loanPayment: monthlyPayment,
    thirdPartyLoanAmount: loanAmount,
    dueAtOrder,
    enrolledInAutopay,
    downPaymentAccount,
    autopayAccount
  } = useOrderSummaryVm(summary);

  return (
    <Summary.Section>
      {(isCash || downPaymentAccount?.accountId) && (
        <>
          <Summary.Heading>Payment Details</Summary.Heading>
          <Summary.Box>
            <PaymentDetailsContainer>
              {downPaymentAccount?.accountId && (
                <div>
                  <Summary.List>
                    <Summary.SummaryListItem
                      label="Payment method"
                      value={`${
                        downPaymentAccount.cardBrand || 'Account'
                      } •••• ${downPaymentAccount.mask}`}
                    />

                    <Summary.SummaryListItem
                      label="Due at order"
                      value={currency(dueAtOrder ?? 0)}
                    />
                  </Summary.List>

                  <Summary.EditButton
                    onClick={() => {
                      setShowSummary(false);
                      navigateToStep(StepName.Payment);
                    }}
                    data-analytics-id="pp_order_summary_edit_payment"
                  >
                    Edit Payment Method
                  </Summary.EditButton>
                </div>
              )}

              {isCash && (
                <div>
                  <Summary.List>
                    <Summary.SummaryListItem
                      label="Payment type"
                      value="Cash"
                    />
                  </Summary.List>

                  <Summary.EditButton
                    onClick={() => {
                      setShowSummary(false);
                      navigateToStep(StepName.CoF);
                    }}
                    data-analytics-id="pp_order_summary_edit_payment_type"
                  >
                    Edit Payment Type
                  </Summary.EditButton>
                </div>
              )}
            </PaymentDetailsContainer>
          </Summary.Box>
        </>
      )}

      {hasPaymentMethodBeenSelected &&
        (isCarvanaFinancing || isThirdParty || isOutsideFinance) && (
          <>
            <Summary.Heading>Loan Details</Summary.Heading>

            <Summary.Box>
              <Summary.List>
                <Summary.SummaryListItem
                  label="Lender"
                  value={`
                    ${isCarvanaFinancing ? 'Carvana' : ''}
                    ${
                      isOutsideFinance
                        ? `${institution || 'Self-Arranged Finance'}`
                        : ''
                    }
                    ${isThirdParty ? `${lenderName || 'Third Party'}` : ''}
                  `}
                />

                {(isCarvanaFinancing || isOutsideFinance) && hasTerms && (
                  <>
                    <Summary.SummaryListItem
                      label="Loan amount"
                      value={currency(amountFinanced || 0)}
                    />
                    <Summary.SummaryListItem
                      label="Cash down payment"
                      value={currency(cashDown || 0)}
                    />

                    {!!(apr && apr > 0) && (
                      <Summary.SummaryListItem
                        label="APR (Interest rate)"
                        value={percent(apr)}
                      />
                    )}

                    {!!(term && term > 0) && (
                      <Summary.SummaryListItem
                        label="Loan length"
                        value={`${term.toString()} months`}
                      />
                    )}

                    {!!(monthlyPayment && monthlyPayment > 0) && (
                      <Summary.SummaryListItem
                        label="Monthly payment"
                        value={currency(monthlyPayment)}
                      />
                    )}
                  </>
                )}

                {isThirdParty && (
                  <>
                    <Summary.SummaryListItem
                      label="Loan amount"
                      value={loanAmount ? currency(loanAmount) : ''}
                    />
                  </>
                )}
              </Summary.List>

              <Summary.EditButton
                onClick={() => {
                  setShowSummary(false);
                  navigateToStep(StepName.CoF);
                }}
                data-analytics-id="pp_order_summary_edit_loan"
              >
                Edit Loan Details
              </Summary.EditButton>
            </Summary.Box>
          </>
        )}

      {isCarvanaFinancing && (
        <>
          <Summary.Heading>Auto Pay</Summary.Heading>
          <Summary.Box>
            <Summary.List>
              <Summary.SummaryListItem
                label="Auto pay"
                value={enrolledInAutopay ? 'Enrolled' : 'Not Enrolled'}
              />

              {autopayAccount?.accountId && (
                <Summary.SummaryListItem
                  label="Auto pay method"
                  value={`Account •••• ${autopayAccount.mask}`}
                />
              )}
            </Summary.List>

            {enrolledInAutopay !== null && (
              <Summary.EditButton
                onClick={() => {
                  setShowSummary(false);
                  navigateToStep(StepName.Autopay);
                }}
                data-analytics-id="pp_order_summary_edit_autopay"
              >
                Edit Auto Pay
              </Summary.EditButton>
            )}
          </Summary.Box>
        </>
      )}
    </Summary.Section>
  );
};

export default PaymentSection;
